import {
  Backdrop,
  Button,
  ClickAwayListener,
  Divider,
  Fade,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterKeys, FilterSection, useGetFiltersQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { PersonalFilterRow } from '@schooly/components/filters';
import { useFlag } from '@schooly/hooks/use-flag';
import { EditIcon, MarkIcon, theme } from '@schooly/style';
import { FC, MouseEventHandler, PropsWithChildren, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useMarkbookFilterContext } from '../../../context/markbook/MarkbookFilterContext';
import { useMarkbookContextNavigation } from './MarkbookEditor/helpers';
import { MarkbookSaveModal } from './MarkbookEditor/MarkbookSaveModal';

export const EDIT_BUTTON_MIN_WIDTH = 90;
export const CREATE_BUTTON_MIN_WIDTH = 140;

type MarkbookHeaderProps = PropsWithChildren<{
  buttonTextId?: string;
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;
  canShowFilter: boolean;
}>;

export const MarkbookHeader: FC<MarkbookHeaderProps> = ({
  canShowFilter,
  children,
  buttonTextId,
  onButtonClick,
}) => {
  const [opened, open, close] = useFlag();
  const [isSaveModalOpened, openSaveModal, closeSaveModal] = useFlag();
  const { filterPreview, filterId, isFilterLoading, form, handleSaveFilter, isUpdatingFilter } =
    useMarkbookFilterContext();
  const { currentStaff, schoolId = '' } = useAuth();
  const { handleNavigateToSavedFilterPreview } = useMarkbookContextNavigation();

  const { data: filters, isLoading: isFiltersLoading } = useGetFiltersQuery(
    {
      schoolId: schoolId,
      relationId: currentStaff?.relation_id ?? '',
      section: FilterSection.Markbook,
    },
    { enabled: !!currentStaff?.relation_id && !!schoolId, refetchOnMount: 'always' },
  );
  const { formatMessage } = useIntl();

  const { handleEditFilter, handleApplyMarkbook, handleChangeFilterData } =
    useMarkbookContextNavigation();

  const defaultColor = opened ? 'white' : undefined;

  const currentMarkbookFilter = useMemo(() => {
    return filterId ? filters?.find((filter) => filter.id === filterId) : null;
  }, [filterId, filters]);
  const currentMarkbookFilterColor = !!filterId ? 'warning.main' : undefined;

  const hasFilters = !!filters?.length && !isFiltersLoading;

  const onSave = useCallback(async () => {
    const res = await handleSaveFilter();
    closeSaveModal();

    if (res?.filter_id) {
      handleNavigateToSavedFilterPreview(res.filter_id);
    }
  }, [closeSaveModal, handleNavigateToSavedFilterPreview, handleSaveFilter]);

  const renderSavedFiltersContent = useCallback(() => {
    const saveButton = (
      <Button
        size="small"
        sx={(theme) => ({
          '.svg-icon': { color: theme.palette.warning.main },
          alignSelf: 'center',
        })}
        startIcon={<MarkIcon />}
        variant="outlined"
        onClick={openSaveModal}
      >
        {formatMessage({ id: 'assessments-markbook-SaveMarkbook' })}
      </Button>
    );
    return (
      <Stack p={2} gap={1} height="100%">
        {hasFilters && (
          <Typography variant="h4">
            {formatMessage({ id: 'assessments-markbook-savedMarkbooks' })}
          </Typography>
        )}
        <Stack
          flex={1}
          gap={0.5}
          sx={{
            '& .MuiChip-root': {
              '& .MuiChip-label': {
                ...theme.typography.body1,
                color: 'common.grey2',
              },
            },
          }}
        >
          {!hasFilters && (
            <Stack alignItems="center" justifyContent="center" gap={2.75} pt={8}>
              <Typography
                variant="h3"
                color="common.grey2"
                textAlign="center"
                width={400}
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {formatMessage({ id: 'assessments-markbook-noSavedMarkbooks' })}
              </Typography>
              {saveButton}
            </Stack>
          )}
          {filters?.map((filter) => (
            <PersonalFilterRow
              currentUser={currentStaff}
              isApplied={filter.id === filterId}
              filterKeys={[
                FilterKeys.Date,
                FilterKeys.Group,
                FilterKeys.AgeGroup,
                FilterKeys.Subject,
              ]}
              schoolId={schoolId}
              filter={filter}
              key={filter.id}
              onEdit={handleEditFilter}
              onApply={handleApplyMarkbook}
            />
          ))}
        </Stack>

        {filterPreview && hasFilters && (
          <>
            <Divider sx={{ my: 1 }} />
            <Button
              size="small"
              sx={(theme) => ({
                '.svg-icon': { color: theme.palette.warning.main },
                alignSelf: 'flex-start',
              })}
              startIcon={<MarkIcon />}
              variant="outlined"
              onClick={openSaveModal}
            >
              {formatMessage({ id: 'assessments-markbook-SaveMarkbook' })}
            </Button>
          </>
        )}
      </Stack>
    );
  }, [
    openSaveModal,
    formatMessage,
    filters,
    filterPreview,
    hasFilters,
    currentStaff,
    filterId,
    schoolId,
    handleEditFilter,
    handleApplyMarkbook,
  ]);

  const renderSavedFilters = () => {
    if (!canShowFilter) return null;

    return isFilterLoading ? (
      <MarkbookSavedFiltersLoading />
    ) : (
      <>
        <Backdrop open={opened} invisible sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} />
        <ClickAwayListener onClickAway={close}>
          <div>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={close}
              open={opened}
              placement="bottom-start"
              componentsProps={{
                tooltip: {
                  sx: (theme) => ({
                    width: 690,
                    maxWidth: 690,
                    height: !hasFilters ? 256 : undefined,
                    borderRadius: theme.spacing(1),
                    border: `1px solid ${theme.palette.common.light3}`,
                    padding: 0,
                    overflow: 'hidden',
                    margin: `${theme.spacing(0.5, 0, 0)} !important`,
                  }),
                },
              }}
              TransitionComponent={Fade}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={renderSavedFiltersContent()}
            >
              <Stack
                sx={(theme) => ({
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: opened ? theme.palette.primary.main : undefined,
                  borderRadius: theme.spacing(1),
                  width: theme.spacing(3.75),
                  height: theme.spacing(3.75),
                })}
              >
                <IconButton
                  sx={{
                    color: defaultColor || currentMarkbookFilterColor,
                  }}
                  inverse
                  onClick={open}
                >
                  <MarkIcon />
                </IconButton>
              </Stack>
            </Tooltip>
          </div>
        </ClickAwayListener>

        <MarkbookSaveModal
          opened={isSaveModalOpened}
          onClose={closeSaveModal}
          onSave={form.handleSubmit(onSave)}
          isSaving={isUpdatingFilter}
        />
      </>
    );
  };

  return (
    <Stack
      data-test-id={'page-header'}
      justifyContent="space-between"
      gap={1.5}
      flexDirection="row"
      alignItems="center"
    >
      {renderSavedFilters()}
      <Typography data-test-id={'page-header-title'} variant="subtitle1" noWrap flex={1}>
        {currentMarkbookFilter?.name ?? formatMessage({ id: 'section-Markbook' })}
      </Typography>

      <Stack flexDirection="row" alignItems="center" gap={2}>
        {children}
        <Button
          sx={{ minWidth: EDIT_BUTTON_MIN_WIDTH }}
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleChangeFilterData}
        >
          {formatMessage({ id: 'action-Edit' })}
        </Button>
        <Button
          sx={{ minWidth: CREATE_BUTTON_MIN_WIDTH }}
          onClick={onButtonClick}
          data-test-id={'page-header-action'}
        >
          {formatMessage({ id: buttonTextId })}
        </Button>
      </Stack>
    </Stack>
  );
};

export const MarkbookSavedFiltersLoading: FC = () => {
  return (
    <IconButton
      disabled
      sx={(theme) => ({ width: theme.spacing(3.75), height: theme.spacing(3.75) })}
    >
      <MarkIcon />
    </IconButton>
  );
};

export const MarkbookHeaderLoading: FC = () => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      data-test-id={'page-header'}
      justifyContent="space-between"
      gap={1.5}
      flexDirection="row"
      alignItems="center"
    >
      <MarkbookSavedFiltersLoading />
      <Typography data-test-id={'page-header-title'} variant="subtitle1" noWrap flex={1}>
        {formatMessage({ id: 'section-Markbook' })}
      </Typography>
      <Stack flexDirection="row" alignItems="center" gap={2}>
        <Skeleton variant="rectangular" width={EDIT_BUTTON_MIN_WIDTH} height={44} />
        <Skeleton variant="rectangular" width={CREATE_BUTTON_MIN_WIDTH} height={44} />
      </Stack>
    </Stack>
  );
};
